/**
 * Thin `fetch` wrapper for the AMLiq Authentication API.
 *
 * Resolves the base URL, attaches JSON + bearer headers, unwraps the
 * `{ success, message, data }` envelope, and normalises every failure into an
 * {@link ApiError} carrying the HTTP status and the server-provided message.
 *
 * Ported from the web client. It deliberately stays a standalone `fetch`
 * client (separate from the app's axios `http`) so the login flow keeps its
 * own envelope handling and error shape exactly as designed.
 */
import type { ApiErrorBody, ApiResponse, ValidationErrors } from './types'
import { env } from '@/config/env'

/** API path prefix, used only for the same-origin fallback. */
const API_PREFIX = '/api/v1'

/**
 * Resolve the API base URL from `VITE_API_BASE_URL`, which already includes the
 * `/api/v1` prefix. Falls back to a same-origin relative `/api/v1` when unset
 * (only valid in the browser).
 */
function resolveBaseUrl(): string {
  const configured = env.apiBaseUrl.trim()
  if (!configured) return API_PREFIX
  return configured.replace(/\/+$/, '')
}

const BASE_URL = resolveBaseUrl()

/** A failed API call — carries the HTTP status and server message. */
export class ApiError extends Error {
  readonly status: number
  /** Present on 422 validation failures. */
  readonly errors?: ValidationErrors | unknown

  constructor(status: number, message: string, errors?: ValidationErrors | unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.errors = errors
  }

  /** No account exists for the given email (`POST /auth/check-email`). */
  get isAccountNotFound(): boolean {
    return this.status === 400
  }

  /** Authentication failed — invalid/expired OTP, token, or code. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** Request body failed schema validation. */
  get isValidation(): boolean {
    return this.status === 422
  }

  /** Too many requests. */
  get isRateLimited(): boolean {
    return this.status === 429
  }

  /** No HTTP response was received (network/CORS failure). */
  get isNetworkError(): boolean {
    return this.status === 0
  }
}

/** Type guard for {@link ApiError}. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

interface RequestOptions {
  method?: 'GET' | 'POST'
  /** JSON-serialisable request body. */
  body?: unknown
  /** Bearer token for endpoints guarded by `bearerAuth`. */
  token?: string
  signal?: AbortSignal
}

/**
 * Perform a request and return the unwrapped `data` payload.
 *
 * @throws {ApiError} on any non-2xx response, `success: false` body, or network
 *   failure. `AbortError` is re-thrown unchanged so callers can ignore cancels.
 */
export async function apiRequest<TData>(
  path: string,
  options: RequestOptions = {},
): Promise<TData> {
  const { method = 'GET', body, token, signal } = options

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    })
  } catch (cause) {
    // Re-throw cancellations so the caller can distinguish them from failures.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(0, 'Unable to connect. Check your internet and try again.', cause)
  }

  // Bodies are JSON for both success and documented errors; tolerate an empty body.
  const payload = (await response.json().catch(() => null)) as
    | ApiResponse<TData>
    | ApiErrorBody
    | null

  if (!response.ok || !payload || payload.success === false) {
    const message =
      payload && typeof payload.message === 'string' && payload.message
        ? payload.message
        : `Request failed (${response.status})`
    const errors = payload && 'errors' in payload ? payload.errors : undefined
    throw new ApiError(response.status, message, errors)
  }

  return payload.data
}
